import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Building2,
  Mail,
  User,
  CreditCard,
  Save,
  Palette,
  Globe,
  Plus,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ExternalLink,
  Settings as SettingsIcon,
  Briefcase
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { INDUSTRY_OPTIONS } from '../../utils/industrySchemas'

const EditClient = () => {
  const navigate = useNavigate()
  const { clientId } = useParams()
  const queryClient = useQueryClient()

  const { data: client, isLoading: clientLoading } = useQuery(
    ['client', clientId],
    () => apiService.get(`/clients/profile?clientId=${clientId}`).then(res => res.data),
    { enabled: !!clientId }
  )

  const { data: billingPlans } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data)
  )

  const { data: clientDomains, refetch: refetchDomains } = useQuery(
    ['client-domains', clientId],
    () => apiService.get('/domains').then(res => {
      const domains = res.data?.domains || []
      return domains.filter(d => d.client_id === clientId)
    }),
    { enabled: !!clientId }
  )

  const { register, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm()
  const subscriptionPlan = watch('subscription_plan')
  const isActive = watch('is_active')

  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [domainSettings, setDomainSettings] = useState({ ssl_enabled: false, contact_email: '', status: 'pending' })

  useEffect(() => {
    if (client) {
      reset({
        company_name: client.company_name,
        subscription_plan: client.subscription_plan || 'basic',
        edit_quota: client.edit_quota || client.monthly_edit_limit || 50,
        brand_colors: client.brand_colors?.[0] || '#3B82F6',
        is_active: client.is_active,
        industry: client.industry || 'general',
      })
    }
  }, [client, reset])

  const updateClientMutation = useMutation(
    (clientData) => apiService.put('/clients/profile', { ...clientData, clientId }),
    {
      onSuccess: () => {
        toast.success('Client updated successfully!')
        queryClient.invalidateQueries(['client', clientId])
        queryClient.invalidateQueries('clients')
        navigate(`/admin/clients/${clientId}`)
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to update client')
    }
  )

  const addDomainMutation = useMutation(
    (data) => apiService.post('/domains', { ...data, client_id: clientId }),
    {
      onSuccess: () => { toast.success('Domain added successfully!'); refetchDomains() },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to add domain')
    }
  )

  const verifyDomainMutation = useMutation(
    (domainId) => apiService.post(`/domains/${domainId}/verify`),
    {
      onSuccess: (data) => {
        if (data.data.message && data.data.domain) {
          toast.success(data.data.message)
        } else {
          toast.error('Verification response was incomplete')
        }
        refetchDomains()
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Verification failed')
    }
  )

  const deleteDomainMutation = useMutation(
    (domainId) => apiService.delete(`/domains/${domainId}`),
    {
      onSuccess: () => { toast.success('Domain deleted successfully!'); refetchDomains() },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to delete domain')
    }
  )

  const updateDomainMutation = useMutation(
    ({ domainId, settings }) => apiService.patch(`/domains/${domainId}`, settings),
    {
      onSuccess: () => {
        toast.success('Domain settings updated successfully!')
        refetchDomains()
        setShowSettingsModal(false)
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to update domain settings')
    }
  )

  const onSubmit = (data) => {
    updateClientMutation.mutate({
      company_name: data.company_name,
      subscription_plan: data.subscription_plan,
      edit_quota: parseInt(data.edit_quota),
      brand_colors: data.brand_colors ? [data.brand_colors] : [],
      is_active: Boolean(data.is_active),
      industry: data.industry || 'general',
    })
  }

  const planOptions = billingPlans ? Object.entries(billingPlans).map(([id, plan]) => ({
    id,
    name: plan.name,
    description: `£${plan.price}/month${plan.custom ? ' · Custom' : ''}`,
    quota: plan.features.editsPerMonth,
    price: plan.price,
    type: plan.custom ? 'custom' : 'standard',
    features: [
      `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} sites`,
      `${plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth} edits/month`,
      plan.features.storage,
      plan.features.support
    ]
  })) : []

  if (clientLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 256 }}>
        <LoadingSpinner size="lg" text="Loading client details..." />
      </div>
    )
  }

  if (!client) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <h3 className="font-medium" style={{ marginBottom: 8 }}>Client Not Found</h3>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>The client you're trying to edit doesn't exist.</p>
        <button className="btn btn-primary" onClick={() => navigate('/admin/clients')}>Back to Clients</button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div className="flex items-center gap-4">
        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => navigate(`/admin/clients/${clientId}`)}>
          <ArrowLeft style={{ width: 20, height: 20 }} />
        </button>
        <div>
          <h1 className="h2">Edit Client</h1>
          <p className="text-muted" style={{ fontSize: 13 }}>Update {client.company_name}'s information and settings</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32 }}>
        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Company Information */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <div className="card card-pad">
                <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                  <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                    <Building2 style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                  </div>
                  <span className="font-semibold">Company Information</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Company Name *</label>
                    <input
                      type="text"
                      {...register('company_name', { required: 'Company name is required' })}
                      className="input"
                      placeholder="Acme Corporation"
                      style={errors.company_name ? { borderColor: 'var(--danger)' } : {}}
                    />
                    {errors.company_name && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.company_name.message}</p>}
                  </div>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Industry <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    <select {...register('industry')} className="input">
                      {INDUSTRY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Brand Color</label>
                    <input
                      type="color"
                      {...register('brand_colors')}
                      style={{ width: '100%', height: 40, border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', padding: 4 }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Domain Management */}
            <DomainManagementSection
              clientDomains={clientDomains}
              onAddDomain={addDomainMutation.mutate}
              onVerifyDomain={verifyDomainMutation.mutate}
              onDeleteDomain={deleteDomainMutation.mutate}
              onUpdateDomain={updateDomainMutation.mutate}
              isAddingDomain={addDomainMutation.isLoading}
              isVerifyingDomain={verifyDomainMutation.isLoading}
              isDeletingDomain={deleteDomainMutation.isLoading}
              isUpdatingDomain={updateDomainMutation.isLoading}
              showSettingsModal={showSettingsModal}
              setShowSettingsModal={setShowSettingsModal}
              selectedDomain={selectedDomain}
              setSelectedDomain={setSelectedDomain}
              domainSettings={domainSettings}
              setDomainSettings={setDomainSettings}
            />

            {/* Subscription Plan */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
              <div className="card card-pad">
                <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                  <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                    <CreditCard style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                  </div>
                  <span className="font-semibold">Subscription Plan</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {planOptions.map((plan) => (
                      <label
                        key={plan.id}
                        style={{
                          position: 'relative', cursor: 'pointer', borderRadius: 'var(--radius)',
                          border: subscriptionPlan === plan.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                          padding: 14, transition: 'all 0.2s',
                          background: subscriptionPlan === plan.id ? 'var(--accent-soft)' : 'var(--surface)'
                        }}
                      >
                        <input type="radio" value={plan.id} {...register('subscription_plan')} style={{ position: 'absolute', opacity: 0 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <p className="font-medium" style={{ fontSize: 13 }}>{plan.name}</p>
                          <p className="text-muted" style={{ fontSize: 11 }}>{plan.description}</p>
                          <p className="font-medium" style={{ fontSize: 12, color: 'var(--accent)' }}>£{plan.price}/month</p>
                          <p style={{ fontSize: 11, color: 'var(--text-soft)' }}>{plan.quota === 'unlimited' ? 'Unlimited' : plan.quota} edits</p>
                          <ul style={{ fontSize: 11, color: 'var(--text-muted)', listStyle: 'none', padding: 0, margin: 0 }}>
                            {plan.features.map((feature, i) => <li key={i}>• {feature}</li>)}
                          </ul>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div>
                    <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Monthly Edit Quota</label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      {...register('edit_quota', { required: 'Edit quota is required', min: { value: 1, message: 'Minimum 1 edit' } })}
                      className="input"
                      placeholder="50"
                      style={errors.edit_quota ? { borderColor: 'var(--danger)' } : {}}
                    />
                    {errors.edit_quota && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.edit_quota.message}</p>}
                    <p className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>Override the default plan quota if needed</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Account Settings */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
              <div className="card card-pad">
                <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                  <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                    <User style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                  </div>
                  <span className="font-semibold">Account Settings</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium" style={{ display: 'block', fontSize: 13 }}>Account Status</label>
                    <p className="text-muted" style={{ fontSize: 12 }}>Enable or disable this client's access to the system</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setValue('is_active', e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
                    />
                    <span className="text-soft" style={{ fontSize: 13 }}>Active</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </form>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
            <div className="card card-pad">
              <p className="font-semibold" style={{ marginBottom: 12 }}>Current Client</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
                <div className="flex items-center gap-3">
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#fff', fontWeight: 500, fontSize: 13 }}>{client.user_name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-medium">{client.user_name}</p>
                    <p className="text-muted" style={{ fontSize: 12 }}>{client.email}</p>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Plan:</span>
                  <span className="font-medium">{planOptions.find(p => p.id === (subscriptionPlan || client.subscription_plan))?.name || subscriptionPlan || client.subscription_plan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Sites:</span>
                  <span className="font-medium">{client.site_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Edits Used:</span>
                  <span className="font-medium">{client.edits_used || 0} / {client.edit_quota || client.monthly_edit_limit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Status:</span>
                  <span className={(isActive !== undefined ? isActive : client.is_active) ? 'badge badge-success' : 'badge badge-danger'}>
                    {(isActive !== undefined ? isActive : client.is_active) ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                className="btn btn-primary w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={updateClientMutation.isLoading}
                style={{ height: 44 }}
              >
                {updateClientMutation.isLoading ? <LoadingSpinner size="sm" /> : (
                  <><Save style={{ width: 16, height: 16, marginRight: 8 }} />Update Client</>
                )}
              </button>
              <button
                className="btn w-full"
                onClick={() => navigate(`/admin/clients/${clientId}`)}
                style={{ height: 44 }}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

const DomainManagementSection = ({
  clientDomains,
  onAddDomain,
  onVerifyDomain,
  onDeleteDomain,
  onUpdateDomain,
  isAddingDomain,
  isVerifyingDomain,
  isDeletingDomain,
  isUpdatingDomain,
  showSettingsModal,
  setShowSettingsModal,
  selectedDomain,
  setSelectedDomain,
  domainSettings,
  setDomainSettings
}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [newSubdomain, setNewSubdomain] = useState('')
  const [useSubdomain, setUseSubdomain] = useState(false)

  const handleAddDomain = () => {
    if (!newDomain.trim()) return
    const domainData = { domain: newDomain.toLowerCase().trim() }
    if (useSubdomain && newSubdomain?.trim()) {
      domainData.subdomain = newSubdomain.toLowerCase().trim()
    }
    onAddDomain(domainData)
    setNewDomain('')
    setNewSubdomain('')
    setUseSubdomain(false)
    setShowAddForm(false)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': case 'active': return <CheckCircle style={{ width: 14, height: 14, color: 'var(--success)' }} />
      case 'pending': return <Clock style={{ width: 14, height: 14, color: 'var(--warning)' }} />
      case 'dns_configured': return <AlertCircle style={{ width: 14, height: 14, color: 'var(--accent)' }} />
      case 'failed': return <XCircle style={{ width: 14, height: 14, color: 'var(--danger)' }} />
      default: return <Clock style={{ width: 14, height: 14, color: 'var(--text-muted)' }} />
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified': case 'active': return 'badge-success'
      case 'pending': return 'badge-warning'
      case 'dns_configured': return 'badge-accent'
      case 'failed': return 'badge-danger'
      default: return ''
    }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.15 }}>
      <div className="card card-pad">
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <div className="flex items-center gap-3">
            <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
              <Globe style={{ width: 20, height: 20, color: 'var(--accent)' }} />
            </div>
            <span className="font-semibold">Custom Domains</span>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAddForm(!showAddForm)}>
            <Plus style={{ width: 14, height: 14, marginRight: 4 }} />Add Domain
          </button>
        </div>

        {showAddForm && (
          <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16, border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Domain Name</label>
              <input
                type="text"
                value={newDomain || ''}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain() } }}
                placeholder="example.com"
                className="input"
                required
              />
              <p className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>Enter domain without http:// or www</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="useSubdomain"
                checked={useSubdomain}
                onChange={(e) => setUseSubdomain(e.target.checked)}
                style={{ width: 14, height: 14, accentColor: 'var(--accent)' }}
              />
              <label htmlFor="useSubdomain" className="text-soft" style={{ fontSize: 13 }}>Use a subdomain instead</label>
            </div>
            {useSubdomain && (
              <div>
                <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Subdomain</label>
                <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                  <input
                    type="text"
                    value={newSubdomain || ''}
                    onChange={(e) => setNewSubdomain(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain() } }}
                    placeholder="www"
                    style={{ flex: 1, padding: '0 12px', height: 36, background: 'var(--surface)', color: 'var(--text)', outline: 'none', border: 'none', fontSize: 13 }}
                    required={useSubdomain}
                  />
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 12px', background: 'var(--surface-2)', color: 'var(--text-muted)', fontSize: 13, borderLeft: '1px solid var(--border)' }}>
                    .{newDomain || 'example.com'}
                  </span>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button type="button" className="btn btn-primary btn-sm" onClick={handleAddDomain} disabled={isAddingDomain || !newDomain.trim()}>
                {isAddingDomain ? <LoadingSpinner size="sm" /> : 'Add Domain'}
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {clientDomains && clientDomains.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {clientDomains.map((domain) => {
              const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
              return (
                <div key={domain.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface)' }}>
                  <div className="flex items-center gap-3">
                    <Globe style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
                    <div>
                      <p className="font-medium" style={{ fontSize: 13 }}>{fullDomain}</p>
                      {domain.assigned_sites && domain.assigned_sites.length > 0 && (
                        <p style={{ fontSize: 11, color: 'var(--accent)', marginBottom: 2 }}>
                          Used by {domain.assigned_sites.length} site{domain.assigned_sites.length > 1 ? 's' : ''}: {domain.assigned_sites.map(s => s.name).join(', ')}
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={`badge ${getStatusBadge(domain.status)}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {getStatusIcon(domain.status)}
                          <span style={{ textTransform: 'capitalize' }}>{domain.status.replace('_', ' ')}</span>
                        </span>
                        {!!domain.ssl_enabled && (
                          <span className="badge badge-success" style={{ fontSize: 11 }}>SSL</span>
                        )}
                      </div>
                      {domain.error_message && (
                        <p style={{ fontSize: 11, color: 'var(--danger)', marginTop: 2 }}>{domain.error_message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {domain.status === 'verified' && domain.azure_published_url && (
                      <a href={domain.azure_published_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }} title="View Live Site">
                        <ExternalLink style={{ width: 16, height: 16 }} />
                      </a>
                    )}
                    {(domain.status === 'pending' || domain.status === 'failed') && (
                      <button
                        onClick={() => onVerifyDomain(domain.id)}
                        disabled={isVerifyingDomain}
                        className="btn btn-primary btn-sm"
                        title="Verify Domain"
                      >
                        {isVerifyingDomain ? <LoadingSpinner size="sm" /> : 'Verify'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation()
                        setSelectedDomain(domain)
                        setDomainSettings({ ssl_enabled: Boolean(domain.ssl_enabled), contact_email: domain.contact_email || '', status: domain.status || 'pending' })
                        setShowSettingsModal(true)
                      }}
                      className="btn btn-ghost btn-icon btn-sm"
                      title="Domain Settings"
                    >
                      <SettingsIcon style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Globe style={{ width: 32, height: 32, color: 'var(--text-faint)', margin: '0 auto 8px' }} />
            <p className="text-soft" style={{ fontSize: 13 }}>No custom domains configured</p>
            <p className="text-muted" style={{ fontSize: 11 }}>Add a domain to connect this client's website to their own URL</p>
          </div>
        )}
      </div>

      {/* Domain Settings Modal */}
      {showSettingsModal && selectedDomain && (
        <div
          className="modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSettingsModal(false) }}
        >
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="font-semibold">
                Domain Settings — {selectedDomain.subdomain ? `${selectedDomain.subdomain}.${selectedDomain.domain}` : selectedDomain.domain}
              </h3>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Domain Info */}
                <div style={{ padding: 14, background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
                  <p className="font-medium" style={{ marginBottom: 8, fontSize: 13 }}>Domain Information</p>
                  <div className="text-soft" style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div>Domain: <span style={{ fontFamily: 'var(--font-mono)' }}>{selectedDomain.subdomain ? `${selectedDomain.subdomain}.${selectedDomain.domain}` : selectedDomain.domain}</span></div>
                    <div>Created: {new Date(selectedDomain.created_at).toLocaleDateString()}</div>
                    {selectedDomain.verified_at && <div>Verified: {new Date(selectedDomain.verified_at).toLocaleDateString()}</div>}
                  </div>
                </div>

                {/* SSL */}
                <div>
                  <label className="flex items-center gap-3" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={domainSettings.ssl_enabled}
                      onChange={(e) => setDomainSettings(prev => ({ ...prev, ssl_enabled: e.target.checked }))}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
                    />
                    <div>
                      <span className="font-medium" style={{ fontSize: 13 }}>Enable SSL Certificate</span>
                      <p className="text-muted" style={{ fontSize: 11 }}>Automatically provision and manage SSL certificate</p>
                    </div>
                  </label>
                </div>

                {/* Contact Email */}
                <div>
                  <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Contact Email</label>
                  <input
                    type="email"
                    value={domainSettings.contact_email}
                    onChange={(e) => setDomainSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                    className="input"
                    placeholder="contact@example.com"
                  />
                </div>

                {/* Domain Status */}
                <div>
                  <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Domain Status</label>
                  <select
                    value={domainSettings.status}
                    onChange={(e) => setDomainSettings(prev => ({ ...prev, status: e.target.value }))}
                    className="input"
                  >
                    <option value="pending">Pending</option>
                    <option value="dns_configured">DNS Configured</option>
                    <option value="verified">Verified</option>
                    <option value="active">Active</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                {/* DNS Info */}
                <div style={{ padding: 14, background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
                  <p className="font-medium" style={{ marginBottom: 8, fontSize: 13 }}>DNS Configuration</p>
                  <div className="text-soft" style={{ fontSize: 12 }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, background: 'var(--surface)', padding: 10, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: 8 }}>
                      <div>Type: CNAME</div>
                      <div>Name: {selectedDomain.subdomain || '@'}</div>
                      <div>Value: your-cdn-provider.com</div>
                    </div>
                    <p style={{ fontSize: 11 }}>Point your domain to the above CNAME record for proper configuration.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <div>
                {(selectedDomain.status === 'pending' || selectedDomain.status === 'failed') && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => { onVerifyDomain(selectedDomain.id); setShowSettingsModal(false) }}
                    disabled={isVerifyingDomain}
                    style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                  >
                    {isVerifyingDomain ? <LoadingSpinner size="sm" /> : 'Force Verify'}
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" className="btn" onClick={() => setShowSettingsModal(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const updates = {}
                    if (domainSettings.ssl_enabled !== selectedDomain.ssl_enabled) updates.ssl_enabled = domainSettings.ssl_enabled
                    if (domainSettings.contact_email !== selectedDomain.contact_email) updates.contact_email = domainSettings.contact_email
                    if (domainSettings.status !== selectedDomain.status) updates.status = domainSettings.status
                    if (Object.keys(updates).length > 0) {
                      onUpdateDomain({ domainId: selectedDomain.id, settings: updates })
                    } else {
                      toast('No changes to save')
                      setShowSettingsModal(false)
                    }
                  }}
                  disabled={isUpdatingDomain}
                >
                  {isUpdatingDomain ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default EditClient
